import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { StoredRoom, JsonData } from '../types';

// Define the database schema
const DATABASE_NAME = 'jsonShareDB';
const DATABASE_VERSION = 1;
const STORE_NAME = 'rooms';

interface JsonShareDB extends DBSchema {
  [STORE_NAME]: {
    key: string;
    value: StoredRoom;
  };
}

let dbPromise: Promise<IDBPDatabase<JsonShareDB>> | null = null;

// Initialize the database
const getDb = (): Promise<IDBPDatabase<JsonShareDB>> => {
  if (!dbPromise) {
    dbPromise = openDB<JsonShareDB>(DATABASE_NAME, DATABASE_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
};

// Save a room to IndexedDB
export const saveRoom = async (room: StoredRoom): Promise<void> => {
  const db = await getDb();
  await db.put(STORE_NAME, room);
};

// Delete a room from IndexedDB
export const deleteRoom = async (key: string): Promise<void> => {
  const db = await getDb();
  await db.delete(STORE_NAME, key);
};

// Get a specific room by key
export const getRoom = async (key: string): Promise<StoredRoom | undefined> => {
  const db = await getDb();
  return db.get(STORE_NAME, key);
};

// Get all rooms from IndexedDB
export const getAllRooms = async (): Promise<StoredRoom[]> => {
  const db = await getDb();
  return db.getAll(STORE_NAME);
};

// Clear all rooms from IndexedDB
export const clearAllRooms = async (): Promise<void> => {
  const db = await getDb();
  await db.clear(STORE_NAME);
};

// Create a new room with initial JSON
export const createRoom = async (
  name: string,
  json: JsonData = {}
): Promise<StoredRoom> => {
  const room: StoredRoom = {
    key: crypto.randomUUID(),
    name,
    json,
    updatedAt: Date.now(),
  };
  await saveRoom(room);
  return room;
};

// Update an existing room's JSON
export const updateRoomJson = async (
  key: string,
  json: JsonData
): Promise<void> => {
  const db = await getDb();
  const room = await db.get(STORE_NAME, key);
  if (room) {
    room.json = json;
    room.updatedAt = Date.now();
    await db.put(STORE_NAME, room);
  }
};
